import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { getRegistrationFormStyles } from './registrationFormStyles';
import { ArrowLeft2, ArrowRight2 } from 'iconsax-react';
import { InputAdornment, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useEventRegistrationTranslation } from '@ui/layout/EventRegistration/hooks/useEventRegistrationTranslation';
import fieldRenderers from './fieldRenderers';
import AdditionalAttendeesGroup from '@ui/components/AdditionalAttendeesGroup';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { checkEmailExistsAsync, createFormResponseAsync } from '@application/features/formResponse/formResponseThunk';
import { toast } from 'react-toastify';
import { useFormPersistence } from '@application/common/useFormPersistence';
import { VideoControlButton } from '@ui/layout/EventRegistration/VideoControlButton';
import EventRegistrationStepper from '@ui/components/EventRegistrationStepper';
import EventRegistrationStepHeader from '@ui/components/EventRegistrationStepper/EventRegistrationStepHeader';
import EventRegistrationContentCard from '@ui/components/EventRegistrationContentCard';
import EventRegistrationFormSummary from './components/EventRegistrationFormSummary';
import { formatSummaryFieldValue, buildAdditionalAttendeesSummaryPeople } from './formatSummaryFieldValue';
import { getRegistrationDateLocale } from '@application/utilities/registration-date-time';
import { FieldType } from '@application/enums/field-types';
import { RegistrationType } from '@application/enums/registration-type';
import LocalStorageService from '@application/common/local-storage-service';
import { checkPickupLocationAvailabilityAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { useEventRegistrationLanguageContext } from '@ui/layout/EventRegistration/EventRegistrationLanguageContext';
import { useEventRegistrationDrawerContext } from '@ui/layout/EventRegistration/EventRegistrationDrawerContext';
const EventRegistrationForm = ({ onSubmitForm, mode = 'register', height, previewLayout }) => {
    const { t, activeLanguageCode } = useEventRegistrationTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isBelowLg = useMediaQuery(theme.breakpoints.down('lg'));
    const isPreviewStacked = mode === 'preview' && (previewLayout === 'tablet' || previewLayout === 'mobile');
    const isStackedLayout = isPreviewStacked || (mode === 'register' && isBelowLg);
    const hideInlineSummary = isStackedLayout;
    const registrationFormStyles = useMemo(() => getRegistrationFormStyles(height, mode, previewLayout, mode === 'register' && isBelowLg, hideInlineSummary), [height, mode, previewLayout, isBelowLg, hideInlineSummary]);
    const navigate = useNavigate();
    const { e } = useParams();
    const dispatch = useAppDispatch();
    const { VDKEventForRegistration } = useAppSelector((state) => state.VDKEvent);
    const { selectedLanguageId, registrationDataLanguageId, eventLanguages } = useEventRegistrationLanguageContext();
    const { setSummarySteps, setShowSummaryPreviewButton } = useEventRegistrationDrawerContext();
    const [hasBlockingEmail, setHasBlockingEmail] = useState(false);
    const [emailLoading, setEmailLoading] = useState({});
    const [emailServerError, setEmailServerError] = useState({});
    const [stepIndex, setStepIndex] = useState(0);
    const [formSteps, setFormSteps] = useState([]);
    // Store referredBy fields grouped by parent company dropdown ID
    const [referredByFieldsByParent, setReferredByFieldsByParent] = useState({});
    const { control, handleSubmit, trigger, watch, clearErrors, setValue, getValues, formState: { errors }, setError } = useFormContext();
    const emailDebounceRef = useRef({});
    const [hasBlockingPickupLocation, setHasBlockingPickupLocation] = useState(false);
    const [pickupLocationLoading, setPickupLocationLoading] = useState({});
    const [pickupLocationError, setPickupLocationError] = useState({});
    // Initialize form persistence
    const formPersistence = useFormPersistence({
        eventId: e || '',
        watch,
        setValue,
        getValues,
        formSteps
    });
    useEffect(() => {
        const registrationDataMatchesLanguage = Boolean(VDKEventForRegistration?.formSteps?.length) &&
            Boolean(selectedLanguageId) &&
            registrationDataLanguageId === selectedLanguageId;
        if (!registrationDataMatchesLanguage) {
            setFormSteps([]);
            return;
        }
        const referredByByParent = {};
        VDKEventForRegistration.formSteps.forEach((step) => {
            step.formFields.forEach((field) => {
                const parentId = field.relatedFormFieldId || field.RelatedFormFieldId;
                if (parentId) {
                    if (!referredByByParent[parentId]) {
                        referredByByParent[parentId] = [];
                    }
                    referredByByParent[parentId].push({
                        ...field,
                        fieldType: field.fieldType?.toString() || field.fieldTypeId?.toString() || '1',
                        isRequired: field.isRequired || field.required || false,
                        fieldOptions: field.fieldOptions || field.options || []
                    });
                }
            });
        });
        setReferredByFieldsByParent(referredByByParent);
        const transformedSteps = VDKEventForRegistration.formSteps.map((step) => ({
            ...step,
            formFields: step.formFields
                .filter((field) => !field.relatedFormFieldId && !field.RelatedFormFieldId)
                .map((field) => {
                const processedField = {
                    ...field,
                    fieldType: field.fieldType?.toString() || field.fieldTypeId?.toString() || '1',
                    isRequired: field.isRequired || field.required || false,
                    fieldOptions: field.fieldOptions || field.options || [],
                    parentOptionId: field.parentOptionId ?? field.ParentOptionId ?? null,
                    parentOptionIds: field.parentOptionIds ?? field.ParentOptionIds ?? []
                };
                if (processedField.fieldType === '18' &&
                    VDKEventForRegistration.eventCompanies &&
                    VDKEventForRegistration.eventCompanies.length > 0) {
                    const eventCompanyOptions = VDKEventForRegistration.eventCompanies.map((company, index) => ({
                        id: company.value,
                        label: company.label,
                        value: index + 1,
                        optionOrder: index + 1,
                        fieldId: field.id
                    }));
                    processedField.fieldOptions = eventCompanyOptions;
                }
                if (processedField.fieldType === '20') {
                    const existingOptionsById = new Map((field.fieldOptions || field.options || []).map((opt) => [String(opt.id), opt]));
                    const pickupLocationOptions = (VDKEventForRegistration.pickupLocations || []).map((loc, index) => ({
                        id: loc.id,
                        label: existingOptionsById.get(String(loc.id))?.label ?? loc.name,
                        description: loc.description ?? loc.address ?? '',
                        value: String(index + 1),
                        optionOrder: index + 1,
                        fieldId: field.id,
                        isClosed: loc.isClosed ?? false
                    }));
                    processedField.fieldOptions = pickupLocationOptions.filter((pl) => !pl.isClosed);
                }
                return processedField;
            })
        }));
        setFormSteps(transformedSteps);
    }, [VDKEventForRegistration, registrationDataLanguageId, selectedLanguageId]);
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const watchedValues = watch();
            Object.keys(errors).forEach((fieldName) => {
                const fieldValue = watchedValues[fieldName];
                if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
                    clearErrors(fieldName);
                }
            });
        }
    }, [errors, watch, clearErrors]);
    useEffect(() => {
        if (formSteps.length > 0) {
            formSteps.forEach((step) => {
                step.formFields.forEach((field) => {
                    if (field.fieldType === FieldType.TOGGLE) {
                        const currentValue = getValues(field.id);
                        if (currentValue === undefined) {
                            setValue(field.id, false);
                        }
                    }
                    if (field.fieldType === FieldType.CHOICE &&
                        field.fieldOptions &&
                        field.fieldOptions.length > 0) {
                        const defaultValue = field.defaultValue !== undefined &&
                            field.defaultValue !== null &&
                            field.defaultValue !== ''
                            ? String(field.defaultValue)
                            : String(field.fieldOptions[0].value);
                        setValue(field.id, defaultValue, { shouldValidate: false });
                    }
                });
            });
        }
    }, [formSteps, getValues, setValue]);
    const getAllFields = () => formSteps.flatMap((step) => (step?.formFields || []));
    const getOptionToParentFieldMap = () => {
        const map = {};
        getAllFields().forEach((field) => {
            (field.fieldOptions || []).forEach((option) => {
                if (option?.id) {
                    map[String(option.id)] = field.id;
                }
            });
        });
        return map;
    };
    const getSelectedOptionIds = (field) => {
        const currentValue = watch(field.id);
        const toComparableValues = (value) => {
            if (typeof value === 'boolean')
                return value ? ['1', 'true'] : ['0', 'false'];
            return [String(value)];
        };
        const selectedValues = Array.isArray(currentValue)
            ? currentValue.flatMap((v) => toComparableValues(v))
            : currentValue !== undefined && currentValue !== null && currentValue !== ''
                ? toComparableValues(currentValue)
                : [];
        const selectedValuesSet = new Set(selectedValues.map((v) => String(v).toLowerCase()));
        if (selectedValues.length === 0)
            return [];
        return (field.fieldOptions || [])
            .filter((option) => selectedValuesSet.has(String(option.value).toLowerCase()))
            .map((option) => String(option.id));
    };
    const isFieldVisible = (field) => {
        const allFields = getAllFields();
        const parentOptionIds = Array.isArray(field.parentOptionIds)
            ? field.parentOptionIds
            : (field.parentOptionId ?? field.ParentOptionId)
                ? [String(field.parentOptionId ?? field.ParentOptionId)]
                : [];
        if (parentOptionIds.length === 0)
            return true;
        const optionToParentField = getOptionToParentFieldMap();
        const parentFieldId = optionToParentField[String(parentOptionIds[0])];
        if (!parentFieldId)
            return false;
        const parentField = getAllFields().find((f) => f.id === parentFieldId);
        if (!parentField)
            return false;
        const selectedParentOptionIds = getSelectedOptionIds(parentField);
        return parentOptionIds.some((id) => selectedParentOptionIds.includes(String(id)));
    };
    const collectAllFormData = () => {
        const allFormData = [];
        formSteps.forEach((step) => {
            const stepFields = [];
            step.formFields.forEach((field) => {
                if (!isFieldVisible(field))
                    return;
                // Additional Attendees: each person's answer to each real sub-question field is its own
                // ordinary FormFieldResponse entry, tagged with which repeated person (instanceIndex) it
                // belongs to - not a single JSON blob. Sub-question fields themselves are excluded from
                // the generic per-field loop below via isFieldVisible/relatedFormFieldId elsewhere.
                if (field.fieldType === FieldType.ADDITIONAL_ATTENDEES) {
                    const people = watch(`${field.id}.people`) || [];
                    const subFieldDefs = referredByFieldsByParent[field.id] || [];
                    people.forEach((person, personIndex) => {
                        if (!person)
                            return;
                        subFieldDefs.forEach((subField) => {
                            const value = person[subField.id];
                            if (value === undefined || value === '')
                                return;
                            stepFields.push({
                                fieldId: subField.id,
                                fieldName: subField.label,
                                value,
                                instanceIndex: personIndex
                            });
                        });
                    });
                    return;
                }
                const fieldValue = watch(field.id);
                if (fieldValue !== undefined && fieldValue !== '') {
                    const fieldData = {
                        fieldId: field.id,
                        fieldName: field.label,
                        value: fieldValue
                    };
                    // Add acknowledgment reason and signature for undertaking fields
                    if (field.fieldType === '19') {
                        const reasonValue = watch(`${field.id}_reason`);
                        if (reasonValue) {
                            fieldData.acknowledgmentReason = reasonValue;
                        }
                        const signatureValue = watch(`${field.id}_signature`);
                        if (signatureValue) {
                            fieldData.signature = signatureValue;
                        }
                    }
                    // Add options as an array if the field has them (for multi-select, checkbox, etc.)
                    if (field.fieldOptions && field.fieldOptions.length > 0) {
                        fieldData.options = field.fieldOptions.map((option) => ({
                            optionId: option.id,
                            optionName: option.label,
                            value: option.value
                        }));
                    }
                    stepFields.push(fieldData);
                }
                // Collect referredBy fields if this is a company dropdown with external company selected
                if (field.fieldType === '18') {
                    const companyValue = watch(field.id);
                    const isExternalCompany = companyValue && isNaN(Number(companyValue));
                    if (isExternalCompany && referredByFieldsByParent[field.id]) {
                        const visibleReferredByFields = referredByFieldsByParent[field.id].filter((f) => f.isEnabled === true);
                        visibleReferredByFields.forEach((refField) => {
                            const refFieldValue = watch(refField.id);
                            // Include the field if it has a value (matching the logic for regular fields)
                            if (refFieldValue !== undefined && refFieldValue !== '') {
                                const refFieldData = {
                                    fieldId: refField.id,
                                    fieldName: refField.label,
                                    value: refFieldValue
                                };
                                stepFields.push(refFieldData);
                            }
                        });
                    }
                }
            });
            allFormData.push({
                stepId: step.id,
                stepName: step.name,
                fields: stepFields
            });
        });
        return allFormData;
    };
    const onSubmit = (data) => {
        const allFormData = collectAllFormData();
        if (!e) {
            toast.error(t('eventRegistration.form.eventIdMissing'));
            return;
        }
        const submissionPayload = {
            eventId: e,
            formResponses: allFormData,
            registrationType: RegistrationType.Self,
            responseLanguage: selectedLanguageId || undefined
        };
        // Save the selected language for this event so it persists when user comes back
        if (e && selectedLanguageId) {
            LocalStorageService.setEventLanguage(e, selectedLanguageId);
        }
        try {
            dispatch(createFormResponseAsync(submissionPayload))
                .unwrap()
                .then((event) => {
                if (event) {
                    formPersistence.clearFormData();
                    navigate(`/event-registration/${e}/success`);
                }
            })
                .catch((error) => {
                const errorMessage = error?.message ||
                    error?.error ||
                    t('eventRegistration.form.submissionFailed') ||
                    'Failed to submit response. Please try again.';
                toast.error(errorMessage);
            });
        }
        catch (error) { }
    };
    const handleNext = async () => {
        if (hasBlockingEmail)
            return;
        if (hasBlockingPickupLocation)
            return;
        // Re-validate all pickup location fields on current step
        const currentPickupFields = (step?.formFields || []).filter((field) => field.fieldType === FieldType.PICKUPLOCATION && isFieldVisible(field));
        if (currentPickupFields.length > 0) {
            const results = await Promise.all(currentPickupFields.map((field) => checkPickupLocationById(field.id, getValues(field.id))));
            if (results.some(Boolean))
                return;
        }
        const currentStepFields = (step?.formFields || []).filter((field) => isFieldVisible(field));
        const fieldNames = currentStepFields.map((field) => field.id);
        // Include referredBy fields that are visible (when external company is selected)
        currentStepFields.forEach((field) => {
            if (field.fieldType === '18') {
                const companyValue = watch(field.id);
                const isExternalCompany = companyValue && isNaN(Number(companyValue));
                if (isExternalCompany && referredByFieldsByParent[field.id]) {
                    const visibleReferredByFields = referredByFieldsByParent[field.id]
                        .filter((f) => f.isEnabled === true || f.isEnabled === undefined)
                        .map((f) => f.id);
                    fieldNames.push(...visibleReferredByFields);
                }
            }
            // Additional Attendees' sub-question answers are registered under nested
            // `${fieldId}.people.${personIndex}.${subFieldId}` paths, not under the parent field's own id
            // - trigger() needs those exact names, otherwise their `required` rule never fires.
            if (field.fieldType === FieldType.ADDITIONAL_ATTENDEES) {
                const subFieldDefs = referredByFieldsByParent[field.id] || [];
                const people = watch(field.id)?.people || [];
                people.forEach((_person, personIndex) => {
                    subFieldDefs.forEach((subField) => {
                        fieldNames.push(`${field.id}.people.${personIndex}.${subField.id}`);
                    });
                });
            }
        });
        const isStepValid = onSubmitForm ? true : await trigger(fieldNames);
        if (isStepValid) {
            if (stepIndex < formSteps.length - 1) {
                setStepIndex(stepIndex + 1);
            }
            else {
                // if props handler exist then in preview mode else event registration mode
                onSubmitForm ? onSubmitForm() : handleSubmit(onSubmit)();
            }
        }
    };
    const handleBack = () => {
        if (stepIndex > 0)
            setStepIndex(stepIndex - 1);
    };
    const step = formSteps[stepIndex];
    const emailFieldIds = useMemo(() => {
        return formSteps.flatMap((formStep) => (formStep.formFields || [])
            .filter((field) => field.fieldType === FieldType.EMAIL)
            .map((field) => field.id));
    }, [formSteps]);
    useEffect(() => {
        if (!e || emailFieldIds.length === 0)
            return;
        const checkEmail = async (name, emailValue) => {
            const stopLoading = () => setEmailLoading((prev) => ({
                ...prev,
                [name]: false
            }));
            if (!emailValue || emailValue.trim() === '') {
                stopLoading();
                setHasBlockingEmail(false);
                setEmailServerError((prev) => ({ ...prev, [name]: null }));
                clearErrors(name);
                return;
            }
            const isValidFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
            if (!isValidFormat) {
                stopLoading();
                setHasBlockingEmail(false);
                setEmailServerError((prev) => ({ ...prev, [name]: 'Invalid email' }));
                setError(name, {
                    type: 'pattern',
                    message: 'Invalid email'
                });
                return;
            }
            clearErrors(name);
            setHasBlockingEmail(false);
            setEmailLoading((prev) => ({
                ...prev,
                [name]: true
            }));
            try {
                const response = await dispatch(checkEmailExistsAsync({ eventId: e, email: emailValue })).unwrap();
                const emailExists = response?.data?.emailExists;
                setHasBlockingEmail(emailExists || false);
                setEmailServerError((prev) => ({
                    ...prev,
                    [name]: emailExists
                        ? response?.message || 'This email is already registered for this event'
                        : null
                }));
                if (emailExists) {
                    setError(name, {
                        type: 'manual',
                        message: response?.message || 'This email is already registered for this event'
                    });
                }
                else {
                    clearErrors(name);
                }
            }
            catch (error) {
                const apiError = error;
                setEmailServerError((prev) => ({
                    ...prev,
                    [name]: apiError?.validationErrors?.[0] || apiError?.error || 'Unable to verify email right now'
                }));
                setError(name, {
                    type: 'manual',
                    message: apiError?.validationErrors?.[0] || apiError?.error || 'Unable to verify email right now'
                });
            }
            finally {
                stopLoading();
            }
        };
        // --- Watch user typing ---
        const subscription = watch((value, { name }) => {
            if (!name || !emailFieldIds.includes(name))
                return;
            const emailValue = value?.[name];
            if (emailDebounceRef.current[name]) {
                clearTimeout(emailDebounceRef.current[name]);
            }
            emailDebounceRef.current[name] = setTimeout(() => {
                void checkEmail(name, emailValue);
            }, 500);
        });
        // --- Run on mount for existing values (page reload) ---
        emailFieldIds.forEach((name) => {
            const emailValue = getValues(name);
            if (emailValue) {
                void checkEmail(name, emailValue);
            }
        });
        return () => {
            subscription.unsubscribe();
            Object.values(emailDebounceRef.current).forEach((timer) => clearTimeout(timer));
        };
    }, [watch, emailFieldIds, e, dispatch, setError, clearErrors, getValues]);
    const isEmailCheckInProgress = useMemo(() => Object.values(emailLoading).some(Boolean), [emailLoading]);
    const pickupLocationFieldIds = useMemo(() => {
        return formSteps.flatMap((formStep) => (formStep.formFields || [])
            .filter((field) => field.fieldType === FieldType.PICKUPLOCATION)
            .map((field) => field.id));
    }, [formSteps]);
    const checkPickupLocationById = async (name, selectedValue) => {
        if (!selectedValue || selectedValue.trim() === '') {
            setHasBlockingPickupLocation(false);
            setPickupLocationError((prev) => ({ ...prev, [name]: null }));
            clearErrors(name);
            return false;
        }
        const allFields = formSteps.flatMap((s) => s.formFields || []);
        const pickupField = allFields.find((f) => f.id === name);
        const matchedOption = (pickupField?.fieldOptions || []).find((opt) => String(opt.value) === String(selectedValue));
        if (!matchedOption) {
            setValue(name, undefined, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false
            });
            setHasBlockingPickupLocation(false);
            setPickupLocationError((prev) => ({ ...prev, [name]: null }));
            clearErrors(name);
            return false;
        }
        const locationId = String(matchedOption.id);
        clearErrors(name);
        setHasBlockingPickupLocation(false);
        setPickupLocationLoading((prev) => ({ ...prev, [name]: true }));
        try {
            const response = await dispatch(checkPickupLocationAvailabilityAsync(locationId)).unwrap();
            const isClosed = response?.data?.isClosed;
            setHasBlockingPickupLocation(isClosed || false);
            setPickupLocationError((prev) => ({
                ...prev,
                [name]: isClosed
                    ? response?.message || t('eventRegistration.form.pickupLocationClosed')
                    : null
            }));
            if (isClosed) {
                setError(name, {
                    type: 'manual',
                    message: response?.message || t('eventRegistration.form.pickupLocationClosed')
                });
            }
            else {
                clearErrors(name);
            }
            return isClosed || false;
        }
        catch (error) {
            const apiError = error;
            const errorMsg = apiError?.validationErrors?.[0] ||
                apiError?.error ||
                t('eventRegistration.form.pickupLocationCheckFailed');
            setPickupLocationError((prev) => ({ ...prev, [name]: errorMsg }));
            setError(name, {
                type: 'manual',
                message: errorMsg || apiError?.error || 'Unable to verify pickupLocation right now'
            });
            return true;
        }
        finally {
            setPickupLocationLoading((prev) => ({ ...prev, [name]: false }));
        }
    };
    useEffect(() => {
        if (!e || pickupLocationFieldIds.length === 0)
            return;
        // Watch fires immediately on dropdown selection — no debounce
        const subscription = watch((value, { name }) => {
            if (!name || !pickupLocationFieldIds.includes(name))
                return;
            void checkPickupLocationById(name, value?.[name]);
        });
        // Run on mount for persisted/pre-filled values
        pickupLocationFieldIds.forEach((name) => {
            const locationId = getValues(name);
            if (locationId) {
                void checkPickupLocationById(name, locationId);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, pickupLocationFieldIds, e, dispatch, setError, clearErrors, getValues, setValue, t]);
    const isPickupLocationLoading = useMemo(() => Object.values(pickupLocationLoading).some(Boolean), [pickupLocationLoading]);
    const getFieldProps = (field, isReferredByField = false, isEmailReferredByField = false) => {
        // Check if this is a referredBy field that is not enabled
        const isEnabled = field.isEnabled === true || field.isEnabled === undefined;
        // If it's a referredBy field and not enabled, don't validate
        if (isReferredByField && !isEnabled) {
            return {
                key: field.id,
                name: field.id,
                control,
                fieldType: field.fieldType,
                label: field.label,
                rules: {}
            };
        }
        const common = {
            key: field.id,
            name: field.id,
            control,
            fieldType: field.fieldType,
            label: field.label,
            rules: {
                required: field.fieldType === '19' || field.fieldType === FieldType.TOGGLE
                    ? false
                    : field.isRequired
                        ? field.requiredErrorMsg || t('eventRegistration.form.fieldRequired')
                        : false,
                validate: (value) => {
                    if (!isFieldVisible(field)) {
                        return true; // Skip validation for hidden branching fields
                    }
                    // For referredBy fields, only validate if they're visible (when external company is selected)
                    if (isReferredByField) {
                        // Check if the parent company dropdown has an external company selected
                        const parentId = field.relatedFormFieldId || field.RelatedFormFieldId;
                        if (parentId) {
                            const companyValue = watch(parentId);
                            const isExternalCompany = companyValue && isNaN(Number(companyValue));
                            // Only validate if external company is selected and field is enabled
                            if (!isExternalCompany || !isEnabled) {
                                return true; // Skip validation if not visible
                            }
                        }
                    }
                    // Email validation for referredBy email field (runs even if field is optional)
                    if (isEmailReferredByField) {
                        if (value && typeof value === 'string' && value.trim() !== '') {
                            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            if (!emailPattern.test(value.trim())) {
                                return t('eventRegistration.form.invalidEmail') || 'Invalid email address';
                            }
                        }
                    }
                    // Only validate if the field is required
                    if (!field.isRequired)
                        return emailServerError[field.id] || true;
                    // Special validation for undertaking fields
                    if (field.fieldType === '19') {
                        // For undertaking fields, both true (acknowledge) and false (decline) are valid
                        // Only undefined/null means no choice made
                        if (value === undefined || value === null) {
                            return (field.requiredErrorMsg ||
                                t('eventRegistration.form.acknowledgeOrDeclineUndertaking'));
                        }
                        return true;
                    }
                    // Special validation for toggle fields - false is a valid answer
                    if (field.fieldType === FieldType.TOGGLE) {
                        if (value === undefined || value === null) {
                            return field.requiredErrorMsg || t('eventRegistration.form.fieldRequired');
                        }
                        return emailServerError[field.id] || true;
                    }
                    // Check for empty values
                    if (value === undefined || value === null || value === '') {
                        return field.requiredErrorMsg || t('eventRegistration.form.fieldRequired');
                    }
                    // Check for empty arrays (for multi-select fields)
                    if (Array.isArray(value) && value.length === 0) {
                        return field.requiredErrorMsg || t('eventRegistration.form.fieldRequired');
                    }
                    // Check for whitespace-only strings
                    if (typeof value === 'string' && value.trim() === '') {
                        return field.requiredErrorMsg || t('eventRegistration.form.fieldRequired');
                    }
                    // Special validation for date fields
                    if (field.fieldType === '4' && value) {
                        common.selectedTone = 'main';
                        const date = new Date(value);
                        if (isNaN(date.getTime())) {
                            return t('eventRegistration.form.validDate');
                        }
                    }
                    // Special validation for time fields
                    if (field.fieldType === '5' && value) {
                        common.selectedTone = 'main';
                        const date = new Date(value);
                        if (isNaN(date.getTime())) {
                            return t('eventRegistration.form.validTime');
                        }
                    }
                    return emailServerError[field.id] || true;
                }
            }
        };
        if (field.fieldOptions) {
            common.options = field.fieldOptions.map((opt) => ({
                label: opt.label,
                value: String(opt.value),
                id: opt.id,
                description: opt.description ?? ''
            }));
        }
        if (field.fieldType === '2') {
            const baseValidate = common.rules?.validate;
            common.rules = {
                ...common.rules,
                pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: t('eventRegistration.form.invalidEmail')
                },
                validate: (value) => {
                    const baseResult = baseValidate ? baseValidate(value) : true;
                    if (baseResult !== true)
                        return baseResult;
                    return emailServerError[field.id] || true;
                }
            };
            common.InputProps = {
                ...(common.InputProps || {}),
                endAdornment: emailLoading[field.id] ? (_jsx(InputAdornment, { position: 'end', children: _jsx(CircularProgress, { size: 18 }) })) : undefined
            };
        }
        if (field.fieldType === '3') {
            common.rules = {
                ...common.rules,
                pattern: {
                    value: /^\+?[0-9\-\s]*$/,
                    message: t('eventRegistration.form.invalidPhoneNumber')
                },
                validate: (value) => {
                    // Only validate if the field is required or has a value
                    if (!field.isRequired && (!value || value === ''))
                        return true;
                    // Check for empty values if required
                    if (field.isRequired && (!value || value === '')) {
                        return field.requiredErrorMsg || t('eventRegistration.form.fieldRequired');
                    }
                    // Check for valid phone characters and + position
                    if (value) {
                        // Check if + is only at the start
                        if (value.includes('+') && value.indexOf('+') !== 0) {
                            return t('eventRegistration.form.plusOnlyAtStart');
                        }
                        // Check for valid characters
                        if (!/^\+?[0-9\-\s]*$/.test(value)) {
                            return t('eventRegistration.form.invalidPhoneNumber');
                        }
                    }
                    return true;
                }
            };
        }
        if (field.fieldType === '6') {
            common.multiline = true;
            common.rows = 4;
        }
        if (field.fieldType === '19') {
            // Undertaking field - only one undertaking field allowed per event
            const undertakingFile = VDKEventForRegistration?.undertakingFiles?.[0];
            common.title = field.documentTitle || field.label || t('eventRegistration.form.document');
            common.fileName = undertakingFile?.name || 'document.pdf';
            common.content = field.documentContent || '';
            common.pdfUrl = undertakingFile?.url || field.pdfUrl || field.documentUrl || '';
            common.defaultAcknowledgeReason =
                field.undertakingToggleLabel || t('undertaking.defaultAcknowledgeReason');
            common.defaultDeclineReason = t('undertaking.defaultDeclineReason');
            common.undertakingToggleLabel = field.undertakingToggleLabel;
            common.undertakingAlertNote = field.undertakingAlertNote;
            // Add callback to handle acknowledgment value and reason
            common.onValueChange = (value, reason) => {
                setValue(field.id, value);
                setValue(`${field.id}_reason`, reason || '');
            };
        }
        // EAutocompleteWithCreate (field type 18)
        if (field.fieldType === '18') {
            const isExternalAllowed = VDKEventForRegistration?.isExternalAllowed || false;
            common.allowCreate = isExternalAllowed;
            common.createOptionText = t('eventRegistration.form.add');
            common.onCreateOption = (label) => ({
                label: label,
                value: isExternalAllowed ? label : label.toLowerCase(),
                isNew: true
            });
        }
        // Pickup location dropdown uses EAutocomplete but should behave as single-select.
        if (field.fieldType === '20') {
            common.multiple = false;
        }
        return common;
    };
    const dateLocale = useMemo(() => getRegistrationDateLocale(eventLanguages ?? [], selectedLanguageId, activeLanguageCode), [eventLanguages, selectedLanguageId, activeLanguageCode]);
    const stepperSteps = useMemo(() => formSteps.map((formStep) => ({ label: formStep.name })), [formSteps]);
    const summarySteps = formSteps.slice(0, stepIndex + 1).map((formStep) => {
        const responses = (formStep.formFields || [])
            .filter((field) => isFieldVisible(field))
            .map((field) => {
            const rawValue = watch(field.id);
            if (rawValue === undefined || rawValue === null || rawValue === '')
                return null;
            if (Array.isArray(rawValue) && rawValue.length === 0)
                return null;
            if (field.fieldType === FieldType.ADDITIONAL_ATTENDEES) {
                const people = rawValue?.people;
                if (!Array.isArray(people) || people.length === 0)
                    return null;
                const subFieldDefs = (referredByFieldsByParent[field.id] || [])
                    .slice()
                    .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                    .map((subField) => ({
                    id: subField.id,
                    label: subField.label,
                    fieldType: subField.fieldType,
                    fieldOrder: subField.fieldOrder,
                    fieldOptions: subField.fieldOptions || [],
                    subFieldRole: subField.subFieldRole ?? subField.SubFieldRole ?? subField.role
                }));
                const summaryPeople = buildAdditionalAttendeesSummaryPeople(people, subFieldDefs, t, dateLocale);
                if (summaryPeople.length === 0)
                    return null;
                return {
                    kind: 'additionalAttendees',
                    label: field.label,
                    people: summaryPeople
                };
            }
            const options = field.fieldOptions || [];
            const renderedValue = formatSummaryFieldValue(rawValue, t, options, {
                fieldType: field.fieldType,
                locale: dateLocale
            });
            if (!renderedValue)
                return null;
            return { kind: 'field', label: field.label, value: renderedValue };
        })
            .filter(Boolean);
        return {
            title: formStep.name,
            responses
        };
    });
    useEffect(() => {
        setShowSummaryPreviewButton(hideInlineSummary);
        return () => setShowSummaryPreviewButton(false);
    }, [hideInlineSummary, setShowSummaryPreviewButton]);
    useEffect(() => {
        if (!hideInlineSummary)
            return;
        setSummarySteps(summarySteps);
    }, [hideInlineSummary, summarySteps, setSummarySteps]);
    return (_jsx(Box, { sx: registrationFormStyles.root, children: _jsxs(Grid, { container: true, spacing: { xs: 1, sm: 1.5, lg: 3 }, sx: registrationFormStyles.formGrid, children: [_jsx(Grid, { size: isStackedLayout
                        ? 12
                        : mode === 'preview' && previewLayout === 'desktop'
                            ? 7
                            : { xs: 12, lg: 7 }, sx: { ...registrationFormStyles.formColumn, order: isStackedLayout ? 1 : undefined }, children: _jsxs(EventRegistrationContentCard, { sx: registrationFormStyles.formCard, children: [_jsx(VideoControlButton, { sx: registrationFormStyles.playIcon }), _jsx(Box, { sx: registrationFormStyles.stepperSection, children: _jsx(EventRegistrationStepper, { steps: stepperSteps, activeStep: stepIndex, hideLabels: isPreviewStacked || undefined }) }), _jsxs(Box, { component: 'form', sx: registrationFormStyles.form, children: [_jsxs(Box, { sx: registrationFormStyles.scrollable, children: [_jsx(EventRegistrationStepHeader, { title: `${step?.name ?? ''}`, description: step?.description, compact: isPreviewStacked || (mode === 'register' && isMobile) }), step?.formFields && step?.formFields?.length > 0 ? (step?.formFields
                                                .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                                                .map((field) => {
                                                if (!isFieldVisible(field))
                                                    return null;
                                                const FieldComponent = fieldRenderers[field.fieldType];
                                                const { key, ...restProps } = getFieldProps(field);
                                                // disable all input fields while only letting user to interact with multi select options for optional chaining
                                                const interactiveFieldTypes = ['7', '8', '9', '10', '11', '12'];
                                                const fieldError = errors[field.id]?.message;
                                                // Check if this is a company dropdown and if external company is selected
                                                const isCompanyDropdown = field.fieldType === '18';
                                                const companyValue = isCompanyDropdown ? watch(field.id) : null;
                                                // External company: value is not a number (original companies use 1, 2, 3...)
                                                const isExternalCompany = isCompanyDropdown && companyValue && isNaN(Number(companyValue));
                                                const referredByFields = isExternalCompany && referredByFieldsByParent[field.id]
                                                    ? referredByFieldsByParent[field.id]
                                                        .filter((f) => f.isEnabled === true || f.isEnabled === undefined) // Only show enabled fields
                                                        .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                                                    : [];
                                                return (_jsxs(_Fragment, { children: [field.fieldType === FieldType.ADDITIONAL_ATTENDEES ? (_jsx(AdditionalAttendeesGroup, { fieldId: field.id, label: field.label, maxAdditionalAttendees: field.maxAdditionalAttendees ?? 2, maxAdditionalAttendeesReachedMessage: field.maxAdditionalAttendeesReachedMessage, subFieldDefinitions: (referredByFieldsByParent[field.id] || [])
                                                                .slice()
                                                                .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                                                                .map((subField) => ({
                                                                id: subField.id,
                                                                label: subField.label,
                                                                fieldType: subField.fieldType,
                                                                isRequired: Boolean(subField.isRequired),
                                                                fieldOptions: subField.fieldOptions || []
                                                            })), control: control, disabled: mode !== 'register' }, field.id)) : FieldComponent ? (_jsx(FieldComponent, { error: fieldError, ...restProps, tooltipText: field?.descriptionForToolTip || '', disabled: mode === 'register'
                                                                ? false
                                                                : !interactiveFieldTypes.includes(field.fieldType) }, key)) : (_jsxs(Box, { sx: { width: '100%', mb: 2 }, children: [_jsx(Typography, { variant: 'body1', sx: { fontWeight: 600 }, children: field.label }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', children: t('eventRegistration.form.placeholderNotSupported') })] }, field.id)), isExternalCompany && referredByFields.length > 0 && (_jsxs(Box, { sx: { width: '100%', mt: 1 }, children: [_jsx(Typography, { variant: 'body2', sx: {
                                                                        fontWeight: 600,
                                                                        color: 'text.primary',
                                                                        mb: 1.5
                                                                    }, children: t('events.referredBy') }), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0 }, children: referredByFields.map((refField) => {
                                                                        const RefFieldComponent = fieldRenderers[refField.fieldType];
                                                                        const isEmailReferredByField = String(refField.fieldType) === String(FieldType.EMAIL);
                                                                        const refFieldProps = getFieldProps(refField, true, isEmailReferredByField);
                                                                        const refFieldError = errors[refField.id]?.message;
                                                                        return RefFieldComponent ? (_jsx(RefFieldComponent, { error: refFieldError, disabled: mode === 'register'
                                                                                ? false
                                                                                : !interactiveFieldTypes.includes(refField.fieldType), ...refFieldProps }, refField.id)) : null;
                                                                    }) })] }))] }));
                                            })) : (_jsx(Typography, { variant: 'body1', color: '#00244D', children: t('eventRegistration.form.noFormFields') }))] }), _jsx(Box, { sx: registrationFormStyles.actionsWrap, children: _jsxs(Box, { sx: registrationFormStyles.actions, children: [stepIndex > 0 && (_jsx(Button, { variant: 'outlined', onClick: handleBack, size: isMobile ? 'medium' : 'large', color: 'inherit', startIcon: _jsx(ArrowLeft2, { size: 18 }), sx: registrationFormStyles.backButton, children: t('eventRegistration.form.back') })), _jsx(Button, { fullWidth: true, endIcon: stepIndex + 1 < formSteps.length ? (_jsx(ArrowRight2, { size: 18 })) : (_jsx(Icon, { icon: 'material-symbols:done-rounded', width: '18', height: '18' })), variant: 'contained', size: isMobile ? 'medium' : 'large', onClick: handleNext, type: 'button', sx: registrationFormStyles.nextButton, disabled: hasBlockingEmail ||
                                                        isEmailCheckInProgress ||
                                                        hasBlockingPickupLocation ||
                                                        isPickupLocationLoading, children: stepIndex + 1 < formSteps.length
                                                        ? t('eventRegistration.form.nextStep')
                                                        : t('eventRegistration.form.submit') })] }) })] })] }) }), !hideInlineSummary && (_jsx(Grid, { size: isStackedLayout
                        ? 12
                        : mode === 'preview' && previewLayout === 'desktop'
                            ? 5
                            : { xs: 12, lg: 5 }, sx: {
                        ...registrationFormStyles.summaryWrap,
                        order: isStackedLayout ? 2 : undefined
                    }, children: _jsx(EventRegistrationFormSummary, { steps: summarySteps, panelSx: registrationFormStyles.summaryPanel, imageWrapSx: registrationFormStyles.summaryImageWrap, contentSx: registrationFormStyles.summaryScroll }) }))] }) }));
};
export default EventRegistrationForm;
